if (!customElements.get('pw-review-header')) {
  customElements.define('pw-review-header', class extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      const on = (node, type, handler) => node.addEventListener(type, handler, { signal: this.abort.signal });
      this.dialogs = [...this.querySelectorAll('dialog')];
      this.pages = [...this.querySelectorAll('[data-pwn-page]')];
      this.stack = [];
      const desktopDetails = [...this.querySelectorAll('.pwn-category, .pwn-localization, .pwn-utility > .pwn-utility-dropdown')];
      const closeDesktop = () => desktopDetails.forEach(details => { details.open = false; });
      const showPage = (name, focusTarget) => {
        this.pages.forEach(page => { page.hidden = page.dataset.pwnPage !== name; });
        this.querySelector('[data-pwn-back]').hidden = name === 'root';
        this.querySelector('.pwn-mobile-pages').scrollTop = 0;
        if (focusTarget) focusTarget.focus();
      };
      on(this, 'click', event => {
        const opener = event.target.closest('[data-pwn-open]');
        if (opener) {
          closeDesktop();
          this.opener = opener;
          const dialog = this.querySelector(`[data-pwn-dialog="${opener.dataset.pwnOpen}"]`);
          this.previousOverflow = document.documentElement.style.overflow;
          if (opener.dataset.pwnOpen === 'menu') { this.stack = []; showPage('root'); }
          dialog.showModal();
          document.documentElement.style.overflow = 'hidden';
          opener.setAttribute('aria-expanded', 'true');
          if (opener.dataset.pwnOpen === 'search') this.querySelector('[data-pwn-query]').focus();
        }
        if (event.target.closest('[data-pwn-close]')) this.dialogs.forEach(dialog => dialog.close());
        const next = event.target.closest('[data-pwn-next]');
        if (next) {
          this.stack.push({ name: next.closest('[data-pwn-page]').dataset.pwnPage, opener: next });
          showPage(next.dataset.pwnNext, this.querySelector('[data-pwn-back]'));
        }
        if (event.target.closest('[data-pwn-back]')) {
          const previous = this.stack.pop();
          if (previous) showPage(previous.name, previous.opener);
        }
        const tab = event.target.closest('[role="tab"]');
        if (tab) this.selectTab(tab);
      });
      this.dialogs.forEach(dialog => {
        on(dialog, 'click', event => {
          if (event.target !== dialog) return;
          const bounds = dialog.getBoundingClientRect();
          if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) dialog.close();
        });
        on(dialog, 'close', () => {
          document.documentElement.style.overflow = this.previousOverflow || '';
          this.opener?.setAttribute('aria-expanded', 'false');
          this.opener?.focus();
          clearTimeout(this.searchTimer);
          this.searchRequest?.abort();
          this.querySelector('[data-pwn-results]').removeAttribute('aria-busy');
        });
      });
      desktopDetails.forEach(details => on(details, 'toggle', () => {
        if (details.open) desktopDetails.forEach(other => { if (other !== details) other.open = false; });
      }));
      on(document, 'click', event => { if (!this.contains(event.target)) closeDesktop(); });
      on(this, 'keydown', event => {
        if (event.key === 'Escape') desktopDetails.forEach(details => {
          if (details.open) { details.open = false; details.querySelector('summary').focus(); }
        });
        const tab = event.target.closest('[role="tab"]');
        if (!tab || !['ArrowUp','ArrowDown','Home','End'].includes(event.key)) return;
        event.preventDefault();
        const tabs = [...tab.closest('[role="tablist"]').querySelectorAll('[role="tab"]')];
        const index = tabs.indexOf(tab);
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 : (index + (event.key === 'ArrowDown' ? 1 : -1) + tabs.length) % tabs.length;
        this.selectTab(tabs[next]);
        tabs[next].focus();
      });
      this.breakpoint = matchMedia('(min-width:1024px)');
      on(this.breakpoint, 'change', () => { this.dialogs.forEach(dialog => dialog.close()); closeDesktop(); });
      this.emptySearch = this.querySelector('[data-pwn-results]').innerHTML;
      on(this.querySelector('[data-pwn-query]'), 'input', () => {
        clearTimeout(this.searchTimer);
        this.searchRequest?.abort();
        this.searchTimer = setTimeout(() => this.search(), 220);
      });
      // Keep a selected feature card visible while editing it in Shopify.
      on(document, 'shopify:block:select', event => {
        if (!this.contains(event.target)) return;
        const details = event.target.closest('.pwn-category');
        if (details) details.open = true;
        const panel = event.target.closest('[role="tabpanel"]');
        if (panel) this.selectTab(this.querySelector(`[aria-controls="${panel.id}"]`));
      });
    }
    selectTab(tab) {
      const mega = tab.closest('.pwn-mega');
      mega.querySelectorAll('[role="tab"]').forEach(item => {
        item.setAttribute('aria-selected', String(item === tab));
        item.tabIndex = item === tab ? 0 : -1;
      });
      mega.querySelectorAll('[role="tabpanel"]').forEach(panel => { panel.hidden = panel.id !== tab.getAttribute('aria-controls'); });
    }
    async search() {
      const query = this.querySelector('[data-pwn-query]').value.trim();
      const results = this.querySelector('[data-pwn-results]');
      const status = this.querySelector('[data-pwn-search-status]');
      if (!query) { results.innerHTML = this.emptySearch; results.removeAttribute('aria-busy'); status.textContent = ''; return; }
      const controller = new AbortController();
      this.searchRequest = controller;
      results.setAttribute('aria-busy', 'true');
      status.textContent = 'Searching…';
      const params = new URLSearchParams({ q: query, section_id: 'pw-review-search-results', 'resources[type]': 'product,page,article', 'resources[limit]': '6' });
      try {
        const response = await fetch(`${this.dataset.predictiveUrl}?${params}`, { signal: controller.signal });
        if (!response.ok) throw new Error('Search unavailable');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const content = html.querySelector('[data-pwn-result-content]');
        if (!content) throw new Error('Search unavailable');
        if (query !== this.querySelector('[data-pwn-query]').value.trim()) return;
        results.replaceChildren(content);
        status.textContent = `${content.querySelector('[data-pwn-result-count]').dataset.pwnResultCount} suggestions available. Tab to explore, or press Enter for all results.`;
      } catch (error) {
        if (error.name === 'AbortError') return;
        const link = document.createElement('a');
        link.href = `${this.dataset.searchUrl}?q=${encodeURIComponent(query)}`;
        link.className = 'pwn-cta';
        link.textContent = `Search all results for “${query}”`;
        results.replaceChildren(link);
        status.textContent = 'Suggestions are unavailable. Press Enter to search.';
      } finally {
        if (this.searchRequest === controller) results.removeAttribute('aria-busy');
      }
    }
    disconnectedCallback() {
      if (this.dialogs?.some(dialog => dialog.open)) document.documentElement.style.overflow = this.previousOverflow || '';
      clearTimeout(this.searchTimer);
      this.searchRequest?.abort();
      this.abort?.abort();
    }
  });
}
